/**
 * Display formats for dates and times.
 *
 * These are moment's *localized* format tokens, so what they render follows the
 * locale `src/i18n/dateTimeLocale.ts` puts moment in — i.e. the language the user
 * picked in their profile. Never hardcode a format at a call site: 09/04/2026 reads
 * as 4 September to most of the world and as 9 April to the US, and a hardcoded
 * `h:mm A` shows a 12-hour clock to locales that expect 24.
 *
 * Examples below are en / fr, for 2026-09-04 20:30:45.
 */
export const dateFormats = {
  /** Numeric date. 09/04/2026 · 04/09/2026 */
  date: 'L',
  /** Abbreviated date. Sep 4, 2026 · 4 sept. 2026 */
  dateMedium: 'll',
  /** Full date. September 4, 2026 · 4 septembre 2026 */
  dateLong: 'LL',
  /** Time. 8:30 PM · 20:30 */
  time: 'LT',
  /** Time with seconds. 8:30:45 PM · 20:30:45 */
  timeWithSeconds: 'LTS',
  /** Abbreviated date and time. Sep 4, 2026 8:30 PM · 4 sept. 2026 20:30 */
  dateTime: 'lll',
  /** Abbreviated date and time with seconds. Sep 4, 2026 8:30:45 PM · 4 sept. 2026 20:30:45 */
  dateTimeWithSeconds: 'll LTS',
  /** Full date and time with seconds. September 4, 2026 8:30:45 PM · 4 septembre 2026 20:30:45 */
  dateTimeLongWithSeconds: 'LL LTS',
  /** Numeric date and time with seconds. 09/04/2026 8:30:45 PM · 04/09/2026 20:30:45 */
  dateNumericTimeWithSeconds: 'L LTS',
  /**
   * Day and month with a time.
   *
   * Uses moment's localized tokens so the day/month order follows the active locale.
   * Sep 4, 2026 8:30 PM · 4 sept. 2026 20:30
   */
  dayMonthTime: 'lll',
  /** As `dayMonthTime`, with seconds. Sep 4, 2026 8:30:45 PM · 4 sept. 2026 20:30:45 */
  dayMonthTimeWithSeconds: 'll LTS',
} as const;

/**
 * Sortable, fixed timestamp — deliberately *not* localized. For internal views where the
 * timestamps are scanned and compared down a column, or correlated with logs, and a
 * left-to-right most-significant-first ordering is worth more than a familiar one.
 */
export const sortableDateTime = 'YYYY-MM-DD HH:mm:ss';

/**
 * Date/time format for `react-datepicker`, which formats with date-fns rather than
 * moment. `P`/`p` are date-fns' localized date and time tokens, resolved against the
 * locale registered as the picker's default.
 */
export const datePickerDateTimeFormat = 'Pp';

/**
 * As `datePickerDateTimeFormat`, for month-and-year pickers. `LLLL` is the standalone
 * month name, which is what a label on its own needs — locales that inflect months
 * (Greek, Polish) use a different form inside a full date than they do alone.
 */
export const datePickerMonthYearFormat = 'LLLL yyyy';
